// (B) Radix Tooltip 백엔드 — antd Tooltip 대체. `.om-tooltip`(Content) + `.om-tooltip-inner`/`.om-tooltip-arrow` 미러링.
// KitThemeHost 의 Tooltip.Provider 필요(INF-2). 시각은 styles/omwTooltip.css(antd v5 다크 툴팁 파리티).
//
// ⚠ 트리거는 항상 span(.om-tooltip-trigger)으로 감싼다: 자식이 문자열(truncated text)·비-forwardRef 컴포넌트
//   (OmwButton/OmwDropdownMenu)·host 요소 등 제각각이라, asChild ref 안전성을 위해 host span 으로 통일(inline).
// ⚠ trigger: Radix Tooltip 은 hover/focus 만 지원. antd 의 'click'/'contextMenu' 는 미지원 → hover 로 수렴
//   (실측 click 포함 사용처는 EmailForm toolbar 2건['hover','click']뿐 → hover 유지, click 은 자식 Dropdown 이 처리).
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { CSSProperties } from 'react'
import type { OmwTooltipPlacement, OmwTooltipProps } from '../api/OmwTooltip'
import { cx } from './cx'
import '../styles/omwTooltip.css'

type SideAlign = { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' }

// antd placement → Radix side/align.
const PLACEMENT: Record<OmwTooltipPlacement, SideAlign> = {
  top: { side: 'top', align: 'center' },
  bottom: { side: 'bottom', align: 'center' },
  left: { side: 'left', align: 'center' },
  right: { side: 'right', align: 'center' },
  topLeft: { side: 'top', align: 'start' },
  topRight: { side: 'top', align: 'end' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomRight: { side: 'bottom', align: 'end' },
  leftTop: { side: 'left', align: 'start' },
  leftBottom: { side: 'left', align: 'end' },
  rightTop: { side: 'right', align: 'start' },
  rightBottom: { side: 'right', align: 'end' },
}

export function PlainTooltip({
  title,
  placement = 'top',
  zIndex,
  arrow = true,
  className,
  children,
}: OmwTooltipProps) {
  // title 이 비면 툴팁 없이 children 그대로(antd 동일).
  if (title == null || title === '') return <>{children}</>

  const { side, align } = PLACEMENT[placement] ?? PLACEMENT.top
  const contentStyle: CSSProperties | undefined = zIndex != null ? { zIndex } : undefined

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        <span className="om-tooltip-trigger">{children}</span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className={cx('om-tooltip', className)}
          side={side}
          align={align}
          sideOffset={4}
          style={contentStyle}
        >
          <div className="om-tooltip-inner">{title}</div>
          {arrow !== false && (
            <TooltipPrimitive.Arrow className="om-tooltip-arrow" width={12} height={6} />
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
